import React, { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { ScreenSize, UI } from "@wwimmo/ui";
import { InvoiceListRowDesktop } from "./InvoiceListRowDesktop";
import { InvoiceListRowMobile } from "./InvoiceListRowMobile";
import styles from "./InvoiceList.module.css";
import { Route, selectRoute } from "src/config/routes";
import { useQuery } from "@apollo/client";
import {
    GetRealestateUserAndAccessData,
    GetRealestateUserAndAccessData_realestateUsersAndAccess
} from "src/api/generated/GetRealestateUserAndAccessData";
import { GET_REALESTATEUSER_AND_ACCESS } from "src/api/ticket";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { Invoice } from "src/stores/krediflow/InvoiceStore";
import { INVOICE_LIST_STATE } from "src/stores/krediflow/InvoiceEnums";
import { ImportInvoices } from "src/screens/creditors/importInvoices/ImportInvoices";
import { useLocation, useHistory } from "react-router-dom";

interface InvoiceListProps {
    onFilterChange: (invoiceListState: INVOICE_LIST_STATE) => void;
}

const InvoiceListBase = (props: InvoiceListProps) => {
    const { onFilterChange } = props;
    const { t } = useTranslation();
    const { uiStore, authStore, invoiceListStore, navStore } = useContext(RootStoreContext);

    const isInternalUrlChange = useRef(false);

    const location = useLocation();
    const history = useHistory();
    const query = new URLSearchParams(location.search);
    const activeTabQueryParam = query.get("tab");

    useEffect(() => {
        // If this change was triggered internally, reset the flag and skip
        if (isInternalUrlChange.current) {
            isInternalUrlChange.current = false;
            return;
        }
        if (activeTabQueryParam === "import" && navStore.hasFeatureKrediFlowPro) {
            // Only update if not already in the import state
            if (invoiceListStore.invoiceFilter.state !== INVOICE_LIST_STATE.IMPORT_DOCUMENTS) {
                invoiceListStore.setFilterState(INVOICE_LIST_STATE.IMPORT_DOCUMENTS);
            }
        }
    }, [activeTabQueryParam, navStore, invoiceListStore]);

    const { data } = useQuery<GetRealestateUserAndAccessData, GetRealestateUserAndAccessData_realestateUsersAndAccess>(
        GET_REALESTATEUSER_AND_ACCESS,
        {}
    );

    useEffect(() => {
        invoiceListStore.setRealestateUserData(data?.realestateUsersAndAccess || []);
    }, [invoiceListStore, data?.realestateUsersAndAccess]);

    useEffect(() => {
        invoiceListStore.setIsBatchProcessing(false);
        invoiceListStore.setCurrentBatchProcessIndex(0);
    }, [invoiceListStore]);

    const getColumnWidths = useCallback(() => {
        const columnWidthsDesktop = [
            navStore.hasFeatureKrediFlowPro ? 0.04 : 0, // Batch Processing Checkbox
            0.04, // Invoice Type Icon
            0.04, // Invoice Number (internal)
            0.1, // Invoice Date
            0.2, // Creditor
            0.12, // Invoice Number
            0.06, // Amount
            0.08, // Due Date
            0.12, // Realestate
            0.1, // Assigned To
            0.1 // Status
        ];
        const columnWidthsLaptopL = [
            navStore.hasFeatureKrediFlowPro ? 0.04 : 0, // Batch Processing Checkbox
            0, // Invoice Type Icon
            0.05, // Invoice Number (internal)
            0.12, // Invoice Date
            0.24, // Creditor
            0.1, // Invoice Number
            0.08, // Amount
            0.1, // Due Date
            0.15, // Realestate
            0.12, // Assigned To
            0 // Status
        ];
        const columnWidthsLaptop = [
            navStore.hasFeatureKrediFlowPro ? 0.04 : 0, // Batch Processing Checkbox
            0, // Invoice Type Icon
            0.04, // Invoice Number (internal)
            0.11, // Invoice Date
            0.29, // Creditor
            0.11, // Invoice Number
            0.09, // Amount
            0.11, // Due Date
            0.21, // Realestate
            0, // Assigned To
            0 // Status
        ];
        const columnWidthsTablet = [
            navStore.hasFeatureKrediFlowPro ? 0.04 : 0, // Batch Processing Checkbox
            0, // Invoice Type Icon
            0.05, // Invoice Number (internal)
            0.14, // Invoice Date
            0.38, // Creditor
            0, // Invoice Number
            0.1, // Amount
            0, // Due Date
            0.28, // Realestate
            0, // Assigned To
            0 // Status
        ];
        return uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP_L
            ? columnWidthsDesktop
            : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP_L
                ? columnWidthsLaptopL
                : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP
                    ? columnWidthsLaptop
                    : columnWidthsTablet;
    }, [uiStore.currentScreenSize, navStore.hasFeatureKrediFlowPro]);

    const columnWidths = getColumnWidths();

    const renderItem = useCallback(
        (invoice: Invoice, screenSize: ScreenSize) => {
            const columnWidths = getColumnWidths();

            const listRows = [
                screenSize === ScreenSize.MOBILE ? (
                    <InvoiceListRowMobile invoice={invoice} key={`row-${invoice.number}`} />
                ) : (
                    <InvoiceListRowDesktop invoice={invoice} key={`row-${invoice.number}`} widths={columnWidths} />
                )
            ];

            return (
                <UI.List.Item
                    key={`item-${invoice.number}`}
                    screenSize={screenSize}
                    rows={listRows}
                    className={styles.ListItem}
                    isClickableItemWithoutChevron
                    to={
                        invoice.number
                            ? selectRoute(Route.invoiceDetails, authStore.user?.role, {
                                invoicenumber: String(invoice.number)
                            })
                            : Route.creditors
                    }
                />
            );
        },
        [authStore.user?.role, getColumnWidths]
    );

    // OnChange method needed for controlled checkbox component - all the logic is in the onClickCheckbox method because only from there the stopPropagation works (preventing row click event)
    const onChangeDummyMethod = useCallback((e: React.ChangeEvent<HTMLInputElement>) => { }, []);

    const toggleSelectAllInvoiceOfState = useCallback(
        (e: React.MouseEvent<HTMLInputElement>) => {
            e.stopPropagation();
            const currentlyDisplayedListState = invoiceListStore.invoiceFilter.state;
            invoiceListStore.toggleSelectAllInvoicesOfState(currentlyDisplayedListState);
        },
        [invoiceListStore]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            if (screenSize === ScreenSize.MOBILE) {
                return <></>;
            }

            const rows = [
                <UI.List.Cell
                    className={`${navStore.hasFeatureKrediFlowPro ? "d-flex justify-content-center" : "d-none"}`}
                    key="batch-all-checkbox"
                    colspan={columnWidths[0]}
                    value={
                        <input
                            type="checkbox"
                            className={styles.BatchProcessCheckbox}
                            onChange={onChangeDummyMethod}
                            onClick={toggleSelectAllInvoiceOfState}
                            checked={
                                invoiceListStore.areAllInvoicesOfStateSelected[invoiceListStore.invoiceFilter.state]
                            }
                        />
                    }
                    truncate
                />,
                <UI.List.Cell
                    className={`${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP_L ? "d-none" : "d-flex"}`}
                    key="type"
                    colspan={columnWidths[1]}
                    value=""
                    truncate
                />,
                <UI.List.Cell key="nr" colspan={columnWidths[2]} value={t("screens.kredi_flow.list.nr")} truncate />,
                <UI.List.Cell
                    key="invoiceDate"
                    colspan={columnWidths[3]}
                    value={
                        uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP
                            ? t("screens.kredi_flow.list.invoice_date_short")
                            : t("screens.kredi_flow.list.invoice_date")
                    }
                    truncate
                />,
                <UI.List.Cell
                    key="creditor"
                    colspan={columnWidths[4]}
                    value={t("screens.kredi_flow.list.creditor")}
                    truncate
                />,
                <UI.List.Cell
                    key="invoiceNumber"
                    className={`${uiStore.currentScreenSize <= SCREEN_SIZE.TABLET ? "d-none" : "d-flex"}`}
                    colspan={columnWidths[5]}
                    value={
                        uiStore.currentScreenSize === SCREEN_SIZE.DESKTOP
                            ? t("screens.kredi_flow.list.invoice_number")
                            : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP_L
                                ? t("screens.kredi_flow.list.invoice_number_short")
                                : t("screens.kredi_flow.list.invoice_number_shorter")
                    }
                    truncate
                />,
                <UI.List.Cell
                    key="amount"
                    colspan={columnWidths[6]}
                    value={t("screens.kredi_flow.list.amount")}
                    truncate
                />,
                <UI.List.Cell
                    key="dueDate"
                    className={`${uiStore.currentScreenSize <= SCREEN_SIZE.TABLET ? "d-none" : "d-flex"}`}
                    colspan={columnWidths[7]}
                    value={t("screens.kredi_flow.list.due_date")}
                    truncate
                />,
                <UI.List.Cell
                    key="realestate"
                    colspan={columnWidths[8]}
                    value={t("screens.kredi_flow.list.realestate")}
                    truncate
                />,
                <UI.List.Cell
                    key="assignedTo"
                    className={uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP ? "d-none" : "d-flex"}
                    colspan={columnWidths[9]}
                    value={t("screens.kredi_flow.list.assigned_to")}
                    truncate
                />,
                <UI.List.Cell
                    key="status"
                    className={`${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP_L ? "d-none" : "d-flex"}`}
                    colspan={columnWidths[10]}
                    value={t("screens.kredi_flow.list.state")}
                    truncate
                />
            ];
            return <UI.List.Header rows={rows} className={`${styles.InvoiceListHeader} p-0`} />;
        },
        [
            columnWidths,
            uiStore.currentScreenSize,
            t,
            onChangeDummyMethod,
            toggleSelectAllInvoiceOfState,
            invoiceListStore.areAllInvoicesOfStateSelected,
            invoiceListStore.invoiceFilter.state,
            navStore.hasFeatureKrediFlowPro
        ]
    );

    const stats = invoiceListStore.invoiceStateCounts;

    const onPageChange = useCallback(
        (page: number) => {
            const offset = (page - 1) * invoiceListStore.pageLimit;
            invoiceListStore.setPageOffset(offset);
        },
        [invoiceListStore]
    );

    const onChangeFilter = useCallback(
        (invoiceFilterStateId: string) => {
            const invoiceFilterStateIdNumber = Number(invoiceFilterStateId);
            const newListState = invoiceFilterStateIdNumber as INVOICE_LIST_STATE;

            const currentParams = new URLSearchParams(location.search);
            if (newListState === INVOICE_LIST_STATE.IMPORT_DOCUMENTS) {
                if (currentParams.get("tab") !== "import") {
                    currentParams.set("tab", "import");
                    history.push({ search: currentParams.toString() });
                }
            } else {
                if (currentParams.has("tab")) {
                    currentParams.delete("tab");
                    history.push({ search: currentParams.toString() });
                }
            }

            if (newListState === INVOICE_LIST_STATE.ADDITIONAL_INVOICES) {
                invoiceListStore.setLoadAllInvoices(true);
                invoiceListStore.loadInvoices();
                onFilterChange(INVOICE_LIST_STATE.PAID);
            } else {
                onFilterChange(invoiceFilterStateIdNumber as INVOICE_LIST_STATE);
            }
        },
        [onFilterChange, invoiceListStore, history, location.search]
    );

    const onTabsChange = useCallback(
        (invoiceFilterStateId: string) => {
            onChangeFilter(invoiceFilterStateId);
        },
        [onChangeFilter]
    );

    const onSelectDropdown = useCallback(
        (invoiceFilterStateId: string) => {
            onChangeFilter(invoiceFilterStateId);
        },
        [onChangeFilter]
    );

    const filterStateItems = useMemo(() => {
        const items = [
            {
                label: `${t("screens.kredi_flow.list_state.new")} ${invoiceListStore.initialLoadInvoicesCompleted ? `(${stats.new})` : ""
                    }`,
                id: String(INVOICE_LIST_STATE.NEW)
            },
            {
                label: `${t("screens.kredi_flow.list_state.to_approve")} ${invoiceListStore.initialLoadInvoicesCompleted ? `(${stats.to_approve})` : ""
                    }`,
                id: String(INVOICE_LIST_STATE.TO_APPROVE)
            },
            {
                label: `${t("screens.kredi_flow.list_state.pay")} ${invoiceListStore.initialLoadInvoicesCompleted ? `(${stats.pay})` : ""
                    }`,
                id: String(INVOICE_LIST_STATE.PAY)
            },
            {
                label: `${t("screens.kredi_flow.list_state.rejected")} ${invoiceListStore.initialLoadInvoicesCompleted ? `(${stats.rejected})` : ""
                    }`,
                id: String(INVOICE_LIST_STATE.REJECTED)
            },
            {
                label: `${t("Alle offenen")} ${invoiceListStore.initialLoadInvoicesCompleted
                    ? `(${stats.new + stats.to_approve + stats.pay + stats.rejected})`
                    : ""
                    }`,
                id: String(INVOICE_LIST_STATE.ALL_OPEN)
            }
        ];

        if (invoiceListStore.loadAllInvoices) {
            items.push(
                {
                    label: `${t("screens.kredi_flow.list_state.paid")} ${invoiceListStore.initialLoadInvoicesCompleted ? `(${stats.paid})` : ""
                        }`,
                    id: String(INVOICE_LIST_STATE.PAID)
                },
                {
                    label: `${t("screens.kredi_flow.list_state.cancelled")} ${invoiceListStore.initialLoadInvoicesCompleted ? `(${stats.cancelled})` : ""
                        }`,
                    id: String(INVOICE_LIST_STATE.CANCELLED)
                },
                {
                    label: `${t("labels.all")} ${invoiceListStore.initialLoadInvoicesCompleted
                        ? `(${stats.new + stats.to_approve + stats.pay + stats.paid + stats.rejected + stats.cancelled})`
                        : ""
                        }`,
                    id: String(INVOICE_LIST_STATE.ALL)
                }
            );
        } else {
            items.push({
                label: t("Weitere ..."),
                id: String(INVOICE_LIST_STATE.ADDITIONAL_INVOICES)
            });
        }

        if (navStore.hasFeatureKrediFlowPro) {
            items.push({
                label: t("screens.kredi_flow.list_state.import_and_processing"),
                id: String(INVOICE_LIST_STATE.IMPORT_DOCUMENTS)
            });
        }

        return items;
    }, [
        t,
        stats,
        invoiceListStore.initialLoadInvoicesCompleted,
        navStore.hasFeatureKrediFlowPro,
        invoiceListStore.loadAllInvoices
    ]);

    return (
        <UI.Card flex={true}>
            {uiStore.isMobile ? (
                <UI.Dropdown
                    id="sort"
                    onSelect={onSelectDropdown}
                    items={filterStateItems.map((item) => ({ label: item.label, value: item.id }))}
                    label={
                        filterStateItems.find((item) => Number(item.id) === invoiceListStore.invoiceFilter.state)
                            ?.label ?? ""
                    }
                    className="mb-2"
                />
            ) : (
                <div className={`${styles.Tabbar} ${navStore.hasFeatureKrediFlowPro ? styles.HasImportTab : ""}`}>
                    <UI.Tabs
                        selectedId={String(invoiceListStore.invoiceFilter.state)}
                        onTabChange={onTabsChange}
                        contents={filterStateItems.map((item) => ({ ...item }))}
                        nowrap
                    ></UI.Tabs>
                </div>
            )}
            {navStore.hasFeatureKrediFlowPro &&
                invoiceListStore.invoiceFilter.state === INVOICE_LIST_STATE.IMPORT_DOCUMENTS ? (
                <ImportInvoices />
            ) : (
                <UI.List.BasicList
                    screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                    items={invoiceListStore.invoices}
                    renderItem={renderItem}
                    renderHeader={renderHeader}
                    totalCount={invoiceListStore.currentTabInvoiceCount}
                    onPageChanged={onPageChange}
                    itemsPerPage={20}
                    emptyListMessage={
                        invoiceListStore.loadInvoicesFailed
                            ? t("screens.kredi_flow.list.invoices_load_failed").toString()
                            : !invoiceListStore.initialLoadInvoicesCompleted || invoiceListStore.isLoadingInvoices
                            ? t("screens.kredi_flow.list.invoices_are_loading").toString()
                            : t("screens.kredi_flow.list.no_invoices_found").toString()
                    }
                />
            )}
        </UI.Card>
    );
};

export const InvoiceList = Sentry.withProfiler(observer(InvoiceListBase));
